import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { DataProviderService } from 'src/app/services/data-provider.service';
import { MessagingService } from 'src/app/services/messaging.service';
import { NetworkService } from 'src/app/services/network.service';
import { ResponseTimeService } from 'src/app/services/response-time.service';
import { RestService } from 'src/app/services/rest.service';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';
import { UserService, Office } from 'src/app/services/user.service';
import { UnreadChatsService } from 'src/app/services/unread-chats.service';
import { environment } from 'src/environments';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit {
  public version: string;
  public user: string;
  public officeName: string;
  public offices: Office[] = [];
  public selectedOfficeId: string;
  public officeFormControl = new FormControl();
  public officeOptions: { label: string; value: string }[] = [];
  public text = '';
  public tryHarderOption = false;
  pendingRequestsCount = 0;
  pendingRequestsSending = false;
  longestResponseTime = 0;
  isModalOpen = false;
  public selectedLanguageLabel = '';
  public hasUnreadChats$: Observable<boolean>;

  public languageList = [
    { key: 'en', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.ENGLISH' },
    { key: 'de', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.GERMAN' },
    { key: 'es', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.SPANISH' },
    { key: 'it', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.ITALIAN' },
    { key: 'nl', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.DUTCH' },
    { key: 'pl', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.POLISH' },
    { key: 'fr', name: 'ADMINISTRATION.USER_PROFILE.LANGUAGES.FRENCH' },
  ];

  constructor(
    private messaging: MessagingService,
    private dataProvider: DataProviderService,
    public networkService: NetworkService,
    private responseTimeService: ResponseTimeService,
    private translate: TranslateService,
    private auth: AuthService,
    private storage: StorageService,
    private rest: RestService,
    private router: Router,
    private stateService: StateService,
    private userService: UserService,
    private unreadChatsService: UnreadChatsService
  ) {}

  ngOnInit(): void {
    this.hasUnreadChats$ = this.unreadChatsService.getHasUnreadChats();

    this.rest.language$.subscribe((val) => {
      if (val && val.length > 0) {
        val = val.replaceAll('"', '');
        this.rest.selectedLanguage = val;

        this.translate.use(val);
      } else {
        this.storage.store('language', 'en');
        this.rest.selectedLanguage = 'en';
        this.translate.use('en');
      }
      this.selectedLanguageLabel = this.languageList.find(
        (language) => language.key === this.rest.selectedLanguage
      )?.name;
    });
    this.version = environment.version;
    this.rest.username$.subscribe((username) => {
      // Do something in ServiceB with the new value
      this.user = username;
    });
    //this.user = this.dataProvider.getUserName();
    this.storage.get('tryHarderOption').then((val) => {
      if (val) {
        this.tryHarderOption = val === 'true';
      }
    });
    this.rest.officeName$.subscribe((officeName) => {
      this.officeName = officeName;
    });

    // Subscribe to offices from RestService
    this.rest.offices$.subscribe((offices) => {
      if (offices && offices.length > 0) {
        this.offices = offices;
        this.officeOptions = offices.map((office: Office) => ({
          label: office.name,
          value: office.id.toString(),
        }));
      }
    });

    // Subscribe to current office ID from RestService
    this.rest.officeId$.subscribe((officeId) => {
      if (officeId) {
        this.selectedOfficeId = officeId;
        if (this.officeFormControl.value !== officeId) {
          this.officeFormControl.setValue(officeId, { emitEvent: false });
        }
      }
    });

    // Subscribe to office form control changes
    this.officeFormControl.valueChanges.subscribe((officeId: string) => {
      if (officeId && officeId !== this.selectedOfficeId) {
        this.changeOffice(officeId);
      }
    });

    this.longestResponseTime =
      this.responseTimeService.getLongestResponseTime();
    this.updateText();
    this.subscribeForPendingEvents();
    this.subscribeForResponseTimeEvents();
  }

  ionViewWillEnter(): void {}

  subscribeForPendingEvents(): void {
    this.messaging.pendingRequestsSent.subscribe(() => {
      this.pendingRequestsSending = false;
      this.updateText();
    });
    this.messaging.pendingRequestsChanged.subscribe((count) => {
      this.pendingRequestsCount = count;
      this.updateText();
    });
    this.messaging.pendingRequestsSending.subscribe((count) => {
      this.pendingRequestsSending = true;
      this.pendingRequestsCount = count;
      this.updateText();
    });
  }

  subscribeForResponseTimeEvents(): void {
    this.messaging.longestResponseTimeChanged.subscribe((time: number) => {
      this.longestResponseTime = time;
      this.updateText();
    });
  }

  setOpen(isOpen: boolean) {
    this.isModalOpen = isOpen;
  }

  onWillDismiss(modalEvent: Event) {
    this.isModalOpen = false;
    this.storage.get('language').then((val) => {
      if (val && val.length > 0) {
        val = val.replaceAll('"', '');
        this.rest.selectedLanguage = val;

        this.translate.use(val);
      } else {
        this.storage.store('language', 'en');
        this.rest.selectedLanguage = 'en';
        this.translate.use('en');
      }
      this.selectedLanguageLabel = this.languageList.find(
        (language) => language.key === this.rest.selectedLanguage
      )?.name;
    });
  }

  changeLanguage(e) {
    this.translate.use(e);
    this.stateService.setValue('language', e);
    this.storage.store('language', e);
    this.rest.selectedLanguage = e;

    this.rest
      .changeLanguage(e)
      .pipe(
        catchError((error) => {
          console.error('Failed to update language on server:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  changeOffice(officeId: string) {
    this.selectedOfficeId = officeId;
    this.stateService.setValue('officeId', officeId);
    this.rest.updateOfficeId(officeId);

    if (this.officeFormControl.value !== officeId) {
      this.officeFormControl.setValue(officeId, { emitEvent: false });
    }

    const selectedOffice = this.offices.find(
      (office) => office.id.toString() === officeId
    );
    if (selectedOffice) {
      this.rest.updateOfficeName(selectedOffice.name);
      this.rest.officeName = selectedOffice.name;
    }

    this.userService
      .setOfficeForUser(officeId)
      .pipe(
        catchError((error) => {
          console.error('Failed to update office on server:', error);
          // Revert the local changes if API call fails
          const currentOfficeId = this.stateService.getValue('officeId');
          if (currentOfficeId && currentOfficeId !== officeId) {
            this.selectedOfficeId = currentOfficeId;
            this.rest.updateOfficeId(currentOfficeId);
            this.officeFormControl.setValue(currentOfficeId, {
              emitEvent: false,
            });
          }
          return of(null);
        })
      )
      .subscribe((result) => {
        // Only navigate and reload if the API call was successful
        if (result !== null) {
          // Navigate to home page after successful office change
          this.router.navigate(['home']).then(() => {
            // Reload the page to refresh all data
            window.location.reload();
          });
        }
      });
  }

  routeToHome() {
    this.router.navigate(['home']);
  }
  routeToNotes() {
    this.router.navigate(['notes']);
  }
  routeToChats() {
    this.router.navigate(['chats']);
  }
  routeToNCs() {
    this.router.navigate(['nc']);
  }
  routeToCheckStock() {
    this.router.navigate(['check-stock']);
  }
  updateText(): void {
    // this.text = '<img class="logopic" src="assets/imgs/EWS-Pestcontrol.png" />';
    // this.text += '<div class="username"><b>' + this.user + '</b>';
    // if (!this.networkService.isOnline()) {
    //   this.text += '<div class="offlineService"><b>SERVICE OFFLINE</b></div>';
    // }
    // this.text += '</div>';
    // if (this.pendingRequestsSending) {
    //   this.text += ' Sending pending requests: ' + this.pendingRequestsCount;
    // } else if (this.pendingRequestsCount > 0) {
    //   this.text += ' Pending requests: ' + this.pendingRequestsCount;
    // }
    // this.text += '<br>';
    // this.text +=
    //   'Longest response time: ' +
    //   this.longestResponseTime.toFixed(0) +
    //   ' ms (click to reset)';
  }

  logout() {
    this.auth.logoutNew();
  }

  onClick(): void {
    this.responseTimeService.resetLongestResponseTime();
  }

  useLanguage(language: string): void {
    this.translate.use(language);
  }
  onTryHarderChanged() {
    this.storage.store('tryHarderOption', this.tryHarderOption);
  }
}
